import React, { useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { ExpenseCategoriesContext } from 'src/components/ExpenseCategoriesInputBox/ExpenseCategoriesInputBox'
import ExpenseCategoryDropdown, {
  ExpenseCategoryDropdownOption,
} from 'src/components/ExpenseCategoryDropdown/ExpenseCategoryDropdown'
import { ExpenseDetailsSourceIcon } from 'src/components/ExpenseDetailsSourceIcon/ExpenseDetailsSourceIcon'
import { translateSingleString } from 'src/localization/Trns'
import { ExpenseDetailsModel } from 'src/models/transaction.models'
import {
  activeAlphabeticalExpenseCategoryFiltersRequestParams,
  ExpenseCategoryType,
  useQueryExpenseCategories,
} from 'src/queries/organizations/expenseCategories.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectIsSelectAllTransactionsFromState } from 'src/redux/selectors/data/transactions.selectors'
import { css } from 'styled-components'

interface ExpenseCategoriesInputsProps {
  className?: string
  context?: ExpenseCategoriesContext
  showUpdates?: boolean
  hideWasOnUpdate?: boolean
  initialExpDetailsMultiple?: Record<
    string,
    Array<ExpenseDetailsModel | undefined>
  >
  expenseDetails?: Record<string, string | undefined>
  expenseDetailsErrors?: Record<
    string,
    Array<React.ReactElement | string> | undefined
  >
  onExpenseCategoryChange?: (categoryId: string, labelId: string) => void
  deselectExpenseCategory?: (categoryId: string) => void
  explicitSelectNone?: boolean
  enforceRequired?: boolean
  showPredictOption?: boolean
  testId?: string
  testIdOptionBox?: string
}

const ExpenseCategoriesInputs: React.FC<ExpenseCategoriesInputsProps> = (
  props,
): React.ReactElement => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  const { data: expCatSchema } = useQueryExpenseCategories({
    filters: activeAlphabeticalExpenseCategoryFiltersRequestParams,
  })
  const allSelected = useSelector(selectIsSelectAllTransactionsFromState)

  // if allSelected, do not use any initialValues or expenseDetails
  // since those do not include off-screen transaction's expense details
  const initialExpCatFields = useMemo((): Record<
    string,
    Array<ExpenseDetailsModel | undefined>
  > => {
    return allSelected ? {} : props.initialExpDetailsMultiple || {}
  }, [props.initialExpDetailsMultiple])
  const containerRefs = useRef<Record<string, HTMLDivElement | null>>({})

  return (
    <div
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;
      `}
    >
      {expCatSchema?.map((expCat, i) => {
        const isOptional =
          props.context === ExpenseCategoriesContext.VIRTUAL_CARD
            ? !expCat.requiredForVirtualCards
            : !expCat.requiredForTransactions
        // Account type from ERP can always be deselected
        const showNoneOption =
          isOptional ||
          !props.enforceRequired ||
          expCat.type === ExpenseCategoryType.Account // TODO steph
        // if selectAll, set hasMultipleValues = true since we cannot calculate the correct init fields
        const hasMultipleValues =
          allSelected ||
          (initialExpCatFields?.[expCat.id] !== undefined
            ? initialExpCatFields?.[expCat.id].length > 1
            : false)
        const initSingleVal =
          !hasMultipleValues &&
          initialExpCatFields?.[expCat.id] !== undefined &&
          initialExpCatFields?.[expCat.id].length > 0
            ? initialExpCatFields?.[expCat.id][0]
            : undefined
        const appliedByAutomation =
          !!initSingleVal?.ruleId || !!initSingleVal?.generatedByPrediction
        return (
          <div
            key={i}
            ref={(el) => {
              containerRefs.current[expCat.id] = el
            }}
            data-testid={`exp-cat-${i}`}
            data-haslabels={expCat.hasLabels}
            data-freetextallowed={expCat.freeTextAllowed}
            data-name={expCat.name}
            css={css`
              :not(:first-child) {
                margin-top: ${'20px'};
              }
            `}
          >
            <ExpenseCategoryDropdown
              expCatId={expCat.id}
              isOptional={isOptional}
              fieldLabel={expCat.name}
              testId={props.testId}
              testIdOptionBox={props.testIdOptionBox}
              tooltip={
                appliedByAutomation ? (
                  <ExpenseDetailsSourceIcon
                    errorMessage={initSingleVal?.errorMessage}
                    generatedByPrediction={initSingleVal.generatedByPrediction}
                    cssProps={css`
                      margin-left: 4px;
                      align-self: flex-start;
                    `}
                  />
                ) : undefined
              }
              hideToolipOnUpdate
              validateNewOption={
                expCat.freeTextAllowed ? (): boolean => true : undefined
              }
              errors={
                props.expenseDetailsErrors !== undefined
                  ? props.expenseDetailsErrors[expCat.id]
                  : undefined
              }
              selectedValue={props.expenseDetails?.[expCat.id]}
              baseValue={
                initialExpCatFields?.[expCat.id] !== undefined &&
                initialExpCatFields?.[expCat.id].length === 1
                  ? // use labelName if labelId doesnt exist, to support free text
                    Array.from(initialExpCatFields?.[expCat.id])[0]?.labelId ||
                    Array.from(initialExpCatFields?.[expCat.id])[0]?.labelName
                  : undefined
              }
              // pass in selected objects so that archived labels properly display in dropdown
              selectedOptions={Object.values(initialExpCatFields)
                .flat()
                .reduce((acc, curr) => {
                  if (curr === undefined) return acc
                  return [
                    ...acc,
                    {
                      value: curr.labelId || curr.labelName, // or name because free text
                      details: {
                        expenseCategoryOption: {
                          id: curr.labelId || curr.labelName, // or name because free text
                          name: curr.labelName,
                          // the following values dont matter for displaying but are needed for type
                          code: curr.labelName, // fake value
                          active: true, // fake value
                          categoryId: curr.categoryId,
                        },
                      },
                    },
                  ]
                }, [] as ExpenseCategoryDropdownOption[])}
              chooseOption={(val: string): void => {
                // TODO: figure out why this null check is performed twice
                if (props.onExpenseCategoryChange) {
                  props.onExpenseCategoryChange(expCat.id, val)
                }
              }}
              placeholder={translateSingleString(
                locale,
                hasMultipleValues
                  ? 'Multiple values'
                  : 'Type or select from list',
              )}
              textInputPlaceholder={translateSingleString(
                locale,
                hasMultipleValues ? 'Multiple values' : 'Type here',
              )}
              freeTextAllowed={expCat.freeTextAllowed}
              hasLabels={expCat.hasLabels}
              deselectAllOptions={
                props.deselectExpenseCategory !== undefined &&
                showNoneOption &&
                !props.explicitSelectNone
                  ? (): void => {
                      props.deselectExpenseCategory?.(expCat.id)
                    }
                  : undefined
              }
              showUpdates={props.showUpdates}
              hideWasOnUpdate={props.hideWasOnUpdate}
              css={css`
                &:nth-child(n + 2) {
                  margin-top: 24px;
                }
              `}
              explicitSelectNone={props.explicitSelectNone}
              showPredictOption={props.showPredictOption}
            />
          </div>
        )
      })}
    </div>
  )
}

export default ExpenseCategoriesInputs
